import { FC, useState } from 'react'
import { Bot, ChevronDown, ChevronUp, FileText, Sparkles, BookOpen, ExternalLink, Pill, ClipboardList, Search, CheckCircle2, AlertCircle } from 'lucide-react'
import { useChat, PubMedCitation, ToolCallStatus } from '../../contexts/ChatContext'
import { MessageAttachment, FileCitation } from '../../types/chat'
import { InlineCitationContent } from './InlineCitationContent'
import { MessageMarkdown, MESSAGE_PROSE_CLASSES } from './shared/MessageMarkdown'
import { cn } from '../../lib/utils'
import { ArtifactCard } from './ArtifactCard'
import { StreamingProgressPanel } from './StreamingProgressPanel'
import { ThinkingPanel } from './ThinkingPanel'
import { MessageActions } from './MessageActions'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'
import { CONTENT_THRESHOLDS } from '../../config/thresholds'

/**
 * Collapsible user message component - Clinical Intelligence System design
 * - Long messages start collapsed based on CONTENT_THRESHOLDS.LONG_MESSAGE_LENGTH
 * - Collapsed messages are resizable via drag
 * - Premium styling with glass effect
 */
const UserMessage: FC<{
  content: string
  timestamp: string
  attachments?: MessageAttachment[]
  onEdit?: () => void
  onRetry?: () => void
  isGenerating?: boolean
}> = ({ content, timestamp, attachments, onEdit, onRetry, isGenerating }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const isLongMessage = content.length > CONTENT_THRESHOLDS.LONG_MESSAGE_LENGTH

  return (
    <div className="flex flex-col items-end animate-fade-in">
      {/* Message bubble */}
      <div className={cn(
        "relative max-w-[85%] sm:max-w-[600px]",
        "bg-primary text-primary-foreground",
        "rounded-2xl rounded-br-md",
        "px-4 py-3",
        "shadow-glow-sm"
      )}>
        {/* Show "Show less" button at top when expanded */}
        {isExpanded && isLongMessage && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(false)}
            className="mb-2 h-6 text-xs p-0 hover:bg-white/10 text-primary-foreground/80"
          >
            <ChevronUp className="h-3 w-3 mr-1" />
            Show less
          </Button>
        )}

        {/* Message content */}
        <div
          className={cn(
            "text-sm leading-relaxed whitespace-pre-wrap break-words overflow-wrap-anywhere",
            !isExpanded && isLongMessage && "max-h-24 min-h-[3rem] overflow-y-auto resize-y"
          )}
          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
          {content.split(/(\[Attached File: .*?\])/g).map((part, i) => {
            const match = part.match(/\[Attached File: (.*?)\]/)
            if (match) {
              return (
                <Badge key={i} variant="secondary" className="my-1 mr-1 inline-flex items-center bg-white/20 text-primary-foreground border-0">
                  <FileText className="h-3 w-3 mr-1" />
                  {match[1]}
                </Badge>
              )
            }
            return <span key={i} className="break-words" style={{ overflowWrap: 'anywhere' }}>{part}</span>
          })}
        </div>

        {/* Show more/less button at bottom */}
        {isLongMessage && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 h-6 text-xs p-0 hover:bg-white/10 text-primary-foreground/80"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-3 w-3 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3 mr-1" />
                Show more
              </>
            )}
          </Button>
        )}

        {/* Attached files display */}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-white/20">
            {attachments.map(att => (
              <Badge
                key={att.id}
                variant="outline"
                className="gap-1.5 text-xs bg-white/10 text-primary-foreground border-white/20"
              >
                <FileText className="h-3 w-3" />
                <span className="max-w-[120px] truncate">{att.name}</span>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Actions and Timestamp row */}
      <div className="flex items-center gap-2 mt-1.5 px-1">
        <MessageActions
          role="user"
          content={content}
          onEdit={onEdit}
          onRetry={onRetry}
          isGenerating={isGenerating}
        />
        <div className="text-[10px] text-muted-foreground">
          {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  )
}

/**
 * Assistant message component - Clinical Intelligence System design
 */
const AssistantMessage: FC<{
  content: string
  timestamp: string
  thinkingContent?: string
  thinkingDurationMs?: number
  artifact?: any
  metadata?: any
  toolCalls?: ToolCallStatus[]
  fileCitations?: FileCitation[]
  onExpandArtifact?: () => void
  onExpandThinking?: () => void
  isGenerating?: boolean
  // True only while a reasoning phase is actively streaming for THIS message. Drives the
  // live (auto-expanded) ThinkingPanel; completed messages omit it so their panel stays
  // collapsed ("View Reasoning"). Never set on non-streaming/history messages.
  isThinking?: boolean
}> = ({ content, timestamp, thinkingContent, thinkingDurationMs, artifact, metadata, toolCalls, fileCitations, onExpandArtifact, onExpandThinking, isGenerating, isThinking }) => {
  return (
    <div className="flex flex-col animate-fade-in">
      {/* Avatar and content */}
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-surface-elevated border border-border/50 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>

        {/* Message content */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Tool calls - shown if any tools were executed */}
          {toolCalls && toolCalls.length > 0 && (
            <div className="space-y-1 text-sm">
              {toolCalls.map((tc) => (
                <div
                  key={tc.id}
                  className={cn(
                    "flex items-center gap-2",
                    "transition-all duration-200",
                    tc.status === 'running' && "text-foreground",
                    tc.status === 'complete' && "text-muted-foreground",
                    tc.status === 'error' && "text-destructive"
                  )}
                >
                  {/* Status indicator */}
                  <div className={cn(
                    "w-1.5 h-1.5 rounded-full flex-shrink-0",
                    tc.status === 'running' && "bg-clinical-amber animate-pulse",
                    tc.status === 'complete' && "bg-clinical-sage",
                    tc.status === 'error' && "bg-destructive"
                  )} />

                  {/* Completion icon */}
                  {tc.status === 'complete' && (
                    <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-clinical-sage" />
                  )}
                  {tc.status === 'error' && (
                    <AlertCircle className="h-3 w-3 flex-shrink-0 text-destructive" />
                  )}

                  {/* Tool name */}
                  <span className="font-medium">
                    {tc.displayName}
                  </span>

                  {/* Query/skill preview */}
                  {(tc.args?.query || tc.args?.skill_name) && (
                    <span className="text-muted-foreground/50 truncate max-w-[180px] text-xs">
                      "{tc.args.skill_name || tc.args.query}"
                    </span>
                  )}

                  {/* Citation count badge */}
                  {tc.status === 'complete' && tc.citationCount !== undefined && tc.citationCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-4 px-1.5 bg-clinical-sage/10 text-clinical-sage border-0"
                    >
                      {tc.citationCount} found
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Thinking section. While a reasoning phase is actively streaming (isThinking)
              we render the live, auto-expanded panel even before the first delta lands —
              matching ThinkingPanel's own streaming-empty contract and the case panel. Once
              streaming ends, isThinking is false and the panel is gated on thinkingContent
              alone (collapsed "View Reasoning"). isStreaming is forwarded so the panel
              auto-expands + auto-scrolls live and collapses by default when done. */}
          {(thinkingContent || isThinking) && (
            <ThinkingPanel
              thinkingContent={thinkingContent}
              isStreaming={isThinking}
              durationMs={thinkingDurationMs}
              onExpandToPanel={onExpandThinking}
            />
          )}

          {/* Artifact card - shown if artifact exists */}
          {artifact && (
            <ArtifactCard
              type={artifact.type}
              title={artifact.title}
              content={artifact.content}
              metadata={metadata}
              onExpand={onExpandArtifact}
            />
          )}

          {/* Text content - with inline citations if available */}
          {content && (
            fileCitations && fileCitations.length > 0 ? (
              <InlineCitationContent
                content={content}
                citations={fileCitations}
                // Shared with MessageMarkdown so the citation and non-citation
                // render paths cannot drift (#196).
                className={cn(...MESSAGE_PROSE_CLASSES)}
              />
            ) : (
              <MessageMarkdown content={content} />
            )
          )}

          {/* Copy button only - Retry moved to bottom of chat */}
          {content && (
            <MessageActions
              role="assistant"
              content={content}
              isGenerating={isGenerating}
            />
          )}
        </div>
      </div>

      {/* Timestamp */}
      <div className="text-[10px] text-muted-foreground mt-1.5 ml-11">
        {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  )
}

/**
 * Collapsible references section shown at the end of chat
 */
const CitationsPanel: FC<{ citations: PubMedCitation[] }> = ({ citations }) => {
  const [isOpen, setIsOpen] = useState(false)

  if (citations.length === 0) return null

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="ml-11 mt-4">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-xs gap-2 bg-surface hover:bg-surface-elevated border border-border/50"
          >
            <BookOpen className="h-3.5 w-3.5" />
            References ({citations.length})
            <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="space-y-2 p-3 rounded-lg bg-surface border border-border/50">
            {citations.map((citation) => (
              <a
                key={citation.pmid}
                href={citation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-2 rounded hover:bg-surface-elevated transition-colors group"
              >
                <span className="flex-shrink-0 w-5 h-5 text-[10px] font-medium rounded bg-primary/10 text-primary flex items-center justify-center">
                  {citation.number}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                    {citation.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {citation.authors_short}. {citation.journal}. {citation.year}
                  </p>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </a>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

interface ChatMessagesProps {
  patientId?: string
  onExpandThinking?: (content: string, durationMs?: number) => void
}

/**
 * Conversation starters for empty state - healthcare-focused natural language prompts
 */
const CONVERSATION_STARTERS = [
  { icon: FileText, label: "Summarize this case", prompt: "Summarize the key findings and current status" },
  { icon: Pill, label: "Check interactions", prompt: "Are there any drug interactions I should be aware of?" },
  { icon: ClipboardList, label: "Draft a note", prompt: "Help me draft a clinical note for this encounter" },
  { icon: Search, label: "Research a topic", prompt: "What does the latest evidence say about " },
]

/**
 * Chat messages display component - Clinical Intelligence System design
 * Renders conversation with premium styling and smart auto-scroll
 */
export const ChatMessages: FC<ChatMessagesProps> = ({ patientId, onExpandThinking }) => {
  const { messages, isGenerating, expandArtifact, currentCitations, setUserInput, retryFromMessage, editMessage, streamingProgress } = useChat()

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center space-y-6 p-8 text-center">
        {/* Bot icon with glow */}
        <div className="relative">
          <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl" />
          <div className="relative rounded-2xl bg-surface-elevated border border-border/50 p-5">
            <Bot className="h-10 w-10 text-primary" />
          </div>
        </div>

        {/* Heading */}
        <div className="space-y-1">
          <h3 className="text-xl font-display font-semibold">How can I help?</h3>
          <p className="text-sm text-muted-foreground">
            {patientId ? "Ask about this patient or try a suggestion below" : "Ask a question or try a suggestion below"}
          </p>
        </div>

        {/* Conversation starters grid */}
        <div className="grid grid-cols-2 gap-2 max-w-sm w-full">
          {CONVERSATION_STARTERS.map((starter) => (
            <button
              key={starter.label}
              onClick={() => setUserInput(starter.prompt)}
              className={cn(
                "flex items-center gap-2 p-3 rounded-xl text-left text-sm",
                "bg-surface hover:bg-surface-elevated",
                "border border-border/50 hover:border-primary/30",
                "transition-all duration-200",
                "group"
              )}
            >
              <starter.icon className="h-4 w-4 text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                {starter.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Identify if the last message is a streaming assistant response
  const lastMessage = messages[messages.length - 1]
  const isLastMessageStreaming = isGenerating && lastMessage?.role === 'assistant'

  // Does the streaming turn have something to render INLINE (so it must NOT be sliced and
  // replaced by the bare progress pill)? True when prose is flowing, OR a reasoning phase is
  // active (streamingProgress.isThinking — the regular chat keeps live reasoning text in
  // streamingProgress, not on the message), OR reasoning has already produced text on the
  // message (thinkingContent set at thinking_complete; keeps the panel from flickering away
  // in the brief gap between reasoning end and the first content_delta). The progress panel
  // then only covers the pre-reasoning "Preparing" gap and tool-only "Working".
  const hasStreamingContent =
    isLastMessageStreaming &&
    (!!lastMessage?.content || streamingProgress.isThinking || !!lastMessage?.thinkingContent)

  // Split messages: render all but last streaming message first, then progress, then streaming message
  const messagesToRender = isLastMessageStreaming ? messages.slice(0, -1) : messages

  return (
    <div className="flex flex-1 flex-col space-y-6 p-4 pt-8">
      {/* Render all messages (or all but last if streaming) */}
      {messagesToRender.map((message, index) => (
        <div
          key={message.id}
          className="flex flex-col"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          {message.role === 'user' ? (
            <UserMessage
              content={message.content}
              timestamp={message.timestamp}
              attachments={message.attachments || message.metadata?.attachments}
              onEdit={() => editMessage(message.id)}
              onRetry={() => retryFromMessage(message.id)}
              isGenerating={isGenerating}
            />
          ) : (
            <AssistantMessage
              content={message.content}
              timestamp={message.timestamp}
              thinkingContent={message.thinkingContent}
              thinkingDurationMs={message.thinkingDurationMs}
              artifact={message.artifact}
              metadata={message.metadata}
              toolCalls={message.metadata?.toolCalls}
              fileCitations={message.fileCitations}
              onExpandArtifact={() => expandArtifact(message)}
              onExpandThinking={message.thinkingContent && onExpandThinking
                ? () => onExpandThinking(message.thinkingContent!, message.thinkingDurationMs)
                : undefined
              }
              isGenerating={isGenerating}
            />
          )}
        </div>
      ))}

      {/* Streaming progress panel - only while generating and no content yet (avoids
          duplicate icons, and avoids mounting the panel just to return null when idle —
          mirrors the case-panel's `showProgress` guard). */}
      {isGenerating && !hasStreamingContent && <StreamingProgressPanel />}

      {/* Streaming assistant response - ONLY render when content actually starts flowing */}
      {hasStreamingContent && lastMessage && (
        <div
          key={lastMessage.id}
          className="flex flex-col"
        >
          <AssistantMessage
            content={lastMessage.content}
            timestamp={lastMessage.timestamp}
            // During live reasoning the message's own thinkingContent is still undefined —
            // the accumulating text lives in streamingProgress.thinkingContent — so fall back
            // to it. After thinking_complete the message carries the final joined buffer and
            // takes precedence.
            thinkingContent={lastMessage.thinkingContent ?? (streamingProgress.thinkingContent || undefined)}
            thinkingDurationMs={lastMessage.thinkingDurationMs}
            // Auto-expand the live ThinkingPanel only while a reasoning phase is actively
            // streaming. Goes false at thinking_complete so the panel collapses once done.
            isThinking={streamingProgress.isThinking}
            artifact={lastMessage.artifact}
            metadata={lastMessage.metadata}
            toolCalls={streamingProgress.toolCalls}
            fileCitations={lastMessage.fileCitations}
            onExpandArtifact={() => expandArtifact(lastMessage)}
            onExpandThinking={(lastMessage.thinkingContent || streamingProgress.thinkingContent) && onExpandThinking
              ? () => onExpandThinking((lastMessage.thinkingContent ?? streamingProgress.thinkingContent)!, lastMessage.thinkingDurationMs)
              : undefined
            }
            isGenerating={isGenerating}
          />
        </div>
      )}

      {/* Citations panel - shown when references are available */}
      {currentCitations.length > 0 && !isGenerating && (
        <CitationsPanel citations={currentCitations} />
      )}

    </div>
  )
}
